import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ToastProvider } from '@xcentral/ui';
import { AuthGuard } from './auth/AuthGuard';
import { msalConfig } from './auth/msalConfig';
import { handleStartupRedirect } from './auth/startupRedirect';
import { installSessionResumeCheck } from './auth/sessionResume';
import { App } from './App';
import { initAppInsights } from './appInsights';
import './styles/globals.css';

initAppInsights();

const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as { account?: { homeAccountId: string } };
    if (payload.account) {
      msalInstance.setActiveAccount(msalInstance.getAccount({ homeAccountId: payload.account.homeAccountId }));
    }
  }
});

async function start() {
  await msalInstance.initialize();

  // Handle any pending redirect response. An AADSTS50105 refusal (user not assigned to the
  // application) is a denial and renders Access denied; every other failure keeps the #14687
  // clear-stale-state self-heal. Both live in handleStartupRedirect (Bug #19757).
  const { accessDenied } = await handleStartupRedirect(msalInstance);

  // Set active account if one exists from a previous session
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Bug #24685 — after a long absence, check the session as soon as the tab is visible again so an
  // expired session re-authenticates on return, not on the next random API call. Registered once.
  installSessionResumeCheck(msalInstance);

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <ToastProvider>
            <AuthGuard redirectAccessDenied={accessDenied}>
              <App />
            </AuthGuard>
          </ToastProvider>
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>,
  );
}

start();
